import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlarmClock, Banknote, Bot, Building2, CalendarDays, Check, ClipboardList, FileText, HardHat, Landmark, LayoutGrid, List, Mail, MessageSquare, User, Wrench } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { statusColor } from '../lib/status';
import { formatUsd, localeUI } from '../lib/format';
import { ClaimChat } from '../components/ClaimChat';
import { ClaimEmailBox } from '../components/ClaimEmailBox';
import { CheckConfirmDialog, type CheckConfirm } from '../components/CheckConfirmDialog';
import { FileViewerModal } from '../components/FileViewerModal';
import { useMyDefaultDepartment } from '../lib/useMyDepartment';
import { useScope, ScopeTabs } from '../lib/scope';
import { useClaimDrawer, ClientNameButton } from '../components/ClaimDrawer';
import { BulkRowCheck, BulkAllCheck, BulkProvider, useBulkSelect } from '../lib/bulkSelect';
import { ClaimBulkBar } from '../components/ClaimBulkBar';
import { BoardCardBridge } from '../components/BoardCardBridge';
import { MortgageBox } from '../components/MortgageBox';
import { InsuranceLink } from '../components/InsuranceCard';
import { ViewSettingsButton, ViewSettingsModal } from '../components/ViewSettingsModal';

export type FollowUpItem = {
  claim_team?: string | null;
  /** Responsable EN EL DEPTO del To Do (team asignado; los teams llevan nombre de persona). */
  dept_team_name?: string | null;
  /** Personas que responden por el claim en ese depto, separadas por coma (las calcula el
   *  backend: integrantes del team, o toda la gente del depto si no trabaja por teams). */
  responsible_names?: string | null;
  /** Status del claim EN ESE depto = la vista del board de la que sale su tarjeta. */
  dept_status?: string | null;
  /** Account manager real del claim (columna propia; el sync de Monday no la toca). */
  account_manager?: string | null;
  id: string;
  stage?: string;
  claim_number: string | null;
  homeowner_name: string | null;
  contractor_name: string | null;
  insurance_company: string | null;
  adjuster_info?: string | null;
  field_adjuster_info?: string | null;
  current_status: string | null;
  lifecycle_status?: string | null;
  assigned_person: string | null;
  days_waiting: number | null;
  /** Cuándo ENTRÓ el claim a este status (la fecha de la fila, pedido del dueño). */
  status_since?: string | null;
  last_follow_up_at: string | null;
  next_follow_up_at: string | null;
  follow_up_count: number;
  priority: 'normal' | 'followup' | 'urgent' | 'manager';
  /** Fecha de envío del cheque ya registrada (info obtenida) → el follow-up se apaga. */
  check_sent_on?: string | null;
  /** Monto registrado junto con la fecha (para comparar cuando llegue el cheque). */
  check_sent_amount_cents?: number | null;
  /** Desenlace del cheque enviado; 'lost' pinta la fila como re-issue. */
  check_state?: string | null;
  check_state_at?: string | null;
  /** Si ya llego, no se ofrecen los botones de desenlace. */
  check_received_on?: string | null;
  /** "Falta un documento": por que esta espera esta parada. */
  waiting_docs_since?: string | null;
  waiting_docs_note?: string | null;
  /** Umbrales del círculo de días (⚙ de la vista): verde hasta N, amarillo hasta M, rojo después. */
  green_max?: number | null;
  yellow_max?: number | null;
  /** To Do de Claims, claims con partner: el follow-up del CONTRACTOR (cada 3 días) venció. */
  contractor_due?: boolean;
  contractor_overdue_days?: number;
  /** Mensajes del CONTRACTOR sin responder (Contractor Inbox): SLA de 1 día. */
  co_msg_count?: number;
  co_msg_hours?: number;
  co_msg_overdue?: boolean;
  /** Cola Mortgage (2026-08-10): banco y loan number a la vista, editables en la fila. */
  mortgage_company?: string | null;
  mortgage_name?: string | null;
  mortgage_loan_number?: string | null;
  mortgage_po_box?: string | null;
  mortgage_check_sent_on?: string | null;
};

/** Etapa del To Do → etapa del cheque en claim_check_dates (fecha + monto de envío). */
const CHECK_STAGE: Record<string, string> = {
  acv: 'acv', depreciation: 'depreciation', mortgage: 'mortgage',
  supplement: 'supplement', 'approved-repair': 'repair',
};

/** Vista de Payments (department_views) que corresponde a cada etapa — para el ⚙ y las plantillas. */
const VIEW_KEY: Record<string, string> = {
  acv: 'Waiting 1st Check',
  depreciation: 'Waiting Depreciation',
  'approved-repair': 'Waiting repair Check',
  supplement: 'Waiting more Payments',
  mortgage: 'Waiting from Mortgage',
};

/**
 * Fecha corta para la fila. Acepta ISO completo o fecha pelada 'YYYY-MM-DD'.
 *
 * La pelada se ancla al MEDIODIA local a proposito: `new Date('2026-08-27')` es medianoche UTC, o
 * sea el 26 en cualquier huso de EE.UU. Esta caja mezcla las dos formas —`check_sent_on` viene de
 * un `to_char` (pelada) y `check_state_at` de un `toISOString()`— asi que sin esto la etiqueta que
 * lee la persona no es el dia que dice la base.
 */
const fmtDay = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return d.toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Círculo de días en el status. SIN fuego (acá el 🔥 es señal de buen trabajo, confundía):
 *  verde hasta `green_max` días, amarillo hasta `yellow_max`, rojo de ahí para arriba.
 *  Los umbrales vienen del ⚙ de la vista (defaults 5/7). */
export function DayDot({ days, greenMax, yellowMax }: {
  days: number | null | undefined; greenMax?: number | null; yellowMax?: number | null;
}) {
  const { t } = useI18n();
  const g = greenMax ?? 5;
  const y = Math.max(yellowMax ?? 7, g);
  const n = days ?? 0;
  const cls = n <= g ? 'green' : n <= y ? 'yellow' : 'red';
  return <span className={`day-dot ${cls}`} title={t('fu.tipDays')}>{n}</span>;
}

/** Título de la columna de status según el depto del To Do: cada equipo ve SU
 *  status (claims → claim status, supplements → supplement status, payments →
 *  payment status); otros deptos, el genérico. */
export function statusColLabel(t: (k: string) => string, dept?: string): string {
  if (dept === 'claims') return t('fu.colClaimStatus');
  if (dept === 'supplements') return t('fu.colSuppStatus');
  if (!dept || dept === 'payments') return t('fu.colPayment');
  return t('fu.colDeptStatus');
}

/** Los To Do de CLAIMS y SUPPLEMENTS trabajan por FOLLOW-UPS vencidos (pedido del dueño): la
 *  fila muestra último/próximo follow-up en vez de "Since", y los días son los días DE ATRASO. */
export const FU_TODO_DEPTS = ['claims', 'supplements'];

/** Días de atraso del próximo follow-up (0 = vence hoy). */
export function daysDue(nextAt: string | null | undefined): number {
  if (!nextAt) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(nextAt).getTime()) / 86400000));
}

/** Leyenda de columnas de la cola — misma plantilla de columnas que las filas (todo alineado). */
export function QueueLegend({ dept }: { dept?: string } = {}) {
  const { t } = useI18n();
  if (dept && FU_TODO_DEPTS.includes(dept)) {
    return (
      <div className="fuq-cols fuq-fu fuq-head">
        <span />
        <span>{t('fu.colClient')}</span>
        <span>{statusColLabel(t, dept)}</span>
        <span>{t('fu.colLastFu')}</span>
        <span>{t('fu.colNextFu')}</span>
        <span>{t('fu.colDaysDue')}</span>
        <span>{t('fu.colManager')}</span>
      </div>
    );
  }
  // Sin dept (colas de pago ACV/Depreciation/…) o dept==='payments' (To Do de Payments): la
  // columna muestra el PAYMENTS MANAGER (assigned_person, "la persona del board de Payments"),
  // no el account manager general del claim — pedido 2026-07-29.
  const isPayments = !dept || dept === 'payments';
  return (
    <div className="fuq-cols fuq-head">
      <span />
      <span>{t('fu.colClient')}</span>
      <span>{statusColLabel(t, dept)}</span>
      <span>{t('fu.colSince')}</span>
      <span>{t('fu.colDays')}</span>
      <span>{isPayments ? t('fu.colPaymentsManager') : t('fu.colManager')}</span>
    </div>
  );
}

type Suggestion = { kind: string; label: string; amountCents: number };

/**
 * FECHA + MONTO del cheque que viene. Arriba se ve desde CUÁNDO está el claim en este status
 * (la fecha de entrada) y al lado se registra la fecha en que la aseguradora ENVIÓ el cheque y
 * el monto (casi siempre se sabe; no es obligatorio). El monto sugerido sale del estimado que la
 * IA ya leyó (net claim para ACV/repair, depreciación para su cola). Al llegar el cheque real,
 * el registro sirve para comparar — la comparación avisa, no bloquea.
 */
/**
 * "PAYMENT RECEIVED" — cuál de los cheques que entraron cierra esta espera.
 *
 * Pedido del dueño (2026-08-27): *"el botón de no payment cámbialo por 'Payment received' […] si
 * le damos payment received que salga la lista de cuáles fueron los cheques que llegaron y
 * seleccionar el cheque con la fecha y ya lo guarda en el database q llegó ese día o ya está
 * guardado"*.
 *
 * Muestra los cheques REALES del claim (los que Payments ya cargó) y, al elegir uno, guarda su
 * fecha como la de llegada de esta etapa. Sin cheques cargados deja poner la fecha a mano: la
 * espera puede cerrarse igual mientras el cheque físico se carga después.
 *
 * MARCA EL TIPO ADIVINADO POR LA IA. `check_type_source` distingue el que clasificó una persona
 * del que puso el lector automático, que en prod acierta 3 de 220 y por defecto dice 'acv'. Elegir
 * a ciegas un cheque mal clasificado cerraría una espera que sigue abierta — y nadie vuelve a
 * buscar lo que la pantalla ya dio por llegado.
 */
type ChequeDelClaim = {
  id: string; amount_cents: string | number; check_type: string | null;
  check_type_source?: string | null; check_number: string | null;
  created_at?: string | null; status: string; imported?: boolean;
};

/**
 * Etapa DEL CHEQUE (la de CHECK_STAGE, no la key de la cola) → tipo de cheque. Elegir un cheque en
 * el diálogo lo clasifica con este tipo, que es la puerta de salida de la espera. `mortgage` no
 * tiene tipo propio: ahí sólo se guarda la fecha.
 * OJO con el plural: la etapa es 'supplement'/'repair' y el tipo 'supplements'/'repairs'.
 */
const TIPO_DE_LA_ETAPA: Record<string, string> = {
  acv: 'acv', depreciation: 'depreciation', supplement: 'supplements', repair: 'repairs',
};

function PaymentReceivedDialog({ claimId, stage, onDone, onCancel }: {
  claimId: string; stage: string; onDone: () => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  const [checks, setChecks] = useState<ChequeDelClaim[] | null>(null);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ checks: ChequeDelClaim[] }>(`/api/checks?claimId=${claimId}`)
      // Los rebotados no cerraron nada, y los IMPORTADOS traen como `created_at` el día en que se
      // corrió la importación de Monday, no el día en que llegó el cheque: elegir uno estamparía
      // como fecha de llegada la fecha de una migración. Todas las demás consultas de cheques del
      // módulo los excluyen igual (`NOT k.imported`).
      .then((r) => setChecks(r.checks.filter((k) => k.status !== 'bounced' && !k.imported)))
      .catch(() => setChecks([]));
  }, [claimId]);

  /**
   * Guardar la llegada es DOS hechos, no uno — y con uno solo el claim no salía de la cola.
   *
   * 1. `check-received-date` sella el día que llegó (lo que pidió el dueño: "seleccionar el cheque
   *    con la fecha y ya lo guarda en el database").
   * 2. Si se eligió un cheque real, se lo CLASIFICA con el tipo de esta etapa
   *    (`check_type_source = 'manual'`), que es exactamente la puerta de salida que mira
   *    `CHEQUE_DE_LA_ETAPA_SQL`: "un cheque que UNA PERSONA clasificó con el tipo de esta etapa
   *    cierra la espera aunque nadie haya cargado la fecha". Sin este segundo paso el claim se
   *    quedaba en la cola con la espera marcada como resuelta: lo peor de los dos mundos.
   */
  async function guardar(d: string, checkId?: string) {
    setBusy(true); setErr(null);
    try {
      const tipo = TIPO_DE_LA_ETAPA[stage];
      if (checkId && tipo) {
        await api.post(`/api/checks/${checkId}/type`, { type: tipo }).catch(() => {
          /* best-effort: si falla la clasificación igual queda la fecha, que es lo que se pidió */
        });
      }
      await api.post(`/api/claims/${claimId}/check-received-date`, { stage, date: d });
      onDone();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); setBusy(false); }
  }

  const dia = (k: ChequeDelClaim) => (k.created_at ? String(k.created_at).slice(0, 10) : '');

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{t('fu.received.title')}</h3>
        {checks === null ? (
          <p className="muted">…</p>
        ) : checks.length === 0 ? (
          <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('fu.received.none')}</p>
        ) : (
          <div style={{ display: 'grid', gap: 6, marginBottom: 12, maxHeight: 260, overflowY: 'auto' }}>
            {checks.map((k) => (
              <button key={k.id} className="btn ghost" disabled={busy || !dia(k)}
                onClick={() => void guardar(dia(k), k.id)}
                style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                <strong style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(Number(k.amount_cents))}</strong>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                  {k.check_type ? k.check_type.toUpperCase() : '—'}
                  {k.check_type_source !== 'manual' && ` (${t('fu.received.aiGuess')})`}
                  {k.check_number ? ` · #${k.check_number}` : ''}
                  {dia(k) ? ` · ${fmtDay(dia(k))}` : ` · ${t('fu.received.noDate')}`}
                </span>
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('fu.received.orDate')}</span>
          <input className="input" type="date" style={{ width: 155 }} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          <button className="btn sm" disabled={busy} onClick={() => void guardar(fecha)}>{t('fu.received.save')}</button>
          <button className="btn ghost sm" onClick={onCancel}>{t('common.cancel')}</button>
        </div>
        {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)', marginTop: 8 }}>{err}</div>}
      </div>
    </div>
  );
}

function CheckSentBox({ claimId, checkStage, item, onSaved }: {
  claimId: string; checkStage: string; item: FollowUpItem; onSaved: () => void;
}) {
  const { t } = useI18n();
  const [date, setDate] = useState(item.check_sent_on ?? '');
  const [amount, setAmount] = useState(item.check_sent_amount_cents != null ? String(item.check_sent_amount_cents / 100) : '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sug, setSug] = useState<Suggestion | null>(null);

  // Monto sugerido del estimado (expected_checks calcula net claim / depreciación pendiente).
  useEffect(() => {
    let alive = true;
    api.get<{ suggestions: Suggestion[] }>(`/api/claims/${claimId}/expected-checks`)
      .then((r) => {
        if (!alive) return;
        const kind = checkStage === 'depreciation' ? 'depreciation' : 'acv';
        setSug(r.suggestions.find((x) => x.kind === kind) ?? null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [claimId, checkStage]);

  const [err, setErr] = useState<string | null>(null);
  /**
   * LA FECHA NO SE GUARDA SIN DECIR CON QUIÉN — y esta caja no lo preguntaba.
   *
   * Reporte del dueño (2026-08-27): "no se guarda la fecha desde el dashboard porque no da la
   * opción de confirmar la información". Exacto: desde la mig 0376 el backend EXIGE el nombre del
   * adjuster y la vía (`adjuster_requerido` / `source_requerido`), y esta caja posteaba solo
   * {stage, date, amount} — o sea que el guardado fallaba SIEMPRE, en la cola y en el dashboard.
   * Las otras dos pantallas que guardan la misma fecha (QueuePage y ClaimPaymentsFull) ya abren
   * CheckConfirmDialog; esta se quedó atrás. Ahora usa el MISMO diálogo: una sola forma de
   * registrar el mismo hecho.
   */
  const [confirmando, setConfirmando] = useState(false);
  const [eligiendo, setEligiendo] = useState(false);
  // La razón se aprende del RECHAZO en vez de adivinarla: la fila de la cola no trae el contador
  // de ediciones, y plomear ese dato hasta acá sería una segunda fuente de la misma regla.
  const [pideRazon, setPideRazon] = useState(false);

  function save() { setErr(null); setConfirmando(true); }

  async function saveConfirmado(conf: CheckConfirm) {
    const d = date || new Date().toISOString().slice(0, 10);
    const amt = parseFloat(amount);
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/claims/${claimId}/check-sent-date`, {
        stage: checkStage, date: d,
        amountCents: Number.isFinite(amt) && amt > 0 ? Math.round(amt * 100) : undefined,
        ...conf,
      });
      setConfirmando(false); setPideRazon(false);
      setSaved(true);
      onSaved();
    } catch (e) {
      // Ya se editó antes: el backend pide la razón y el diálogo sigue abierto con ese campo.
      if (e instanceof ApiError && e.message === 'razon_requerida') { setPideRazon(true); return; }
      // Un fallo silencioso acá se leía como "el botón no funciona" (reporte 2026-08-10).
      setErr(e instanceof ApiError ? e.message : 'Error');
      setConfirmando(false);
    } finally { setBusy(false); }
  }

  /** "Under deductible" (pedido de Manuel 2026-08-10): el total quedó por debajo del deducible —
   *  no viene ningún cheque. Saca el claim de esta cola (payment status → Repair (No Payment to
   *  HO or CO)) y deja la nota en los updates de Payments con quién lo decidió. */
  async function underDeductible() {
    if (!window.confirm(t('fu.underDedConfirm'))) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/claims/${claimId}/payment-status`, { status: 'Repair (No Payment to HO or CO)' });
      await api.post(`/api/claims/${claimId}/chat`, {
        department: 'payments',
        body: '💵 Under deductible — the claim total is below the deductible, so no check is coming. Moved to Repair (No Payment to HO or CO).',
      }).catch(() => { /* la nota es best-effort: el movimiento ya quedó */ });
      onSaved();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  /**
   * "FALTA UN DOCUMENTO" — por qué esta espera está parada (pedido del dueño, 2026-08-27).
   *
   * Textual: *"estamos esperando a que se suplemente para pedir un documento llamado final invoice
   * al contratista, o esperando documentos que faltan. Sí deberían estar en la pestaña de
   * depreciación, pero sería bueno colocarle un color diferente a la barra […] para saber que falta
   * documento y por eso no se ha enviado la depreciación"*.
   *
   * El claim NO se mueve: sigue en su cola y en su barra, solo que ahora dice por qué está parado.
   * Es la diferencia entre un reclamo de gestión ("nadie llamó") y una espera legítima con otro
   * dueño ("el contratista todavía no mandó el papel"), que hasta hoy se veían idénticas.
   *
   * Pide la nota al marcar —qué documento falta— porque una bandera sin el nombre del papel obliga
   * a abrir el claim para saber qué se está esperando, que es exactamente el clic que la bandera
   * viene a ahorrar. Desmarcar no pregunta nada.
   */
  const faltaDoc = Boolean(item.waiting_docs_since);
  async function toggleFaltaDoc() {
    let nota: string | null = null;
    if (!faltaDoc) {
      nota = window.prompt(t('fu.docs.ask')) ?? null;
      if (nota === null) return; // canceló
    } else if (!window.confirm(t('fu.docs.clearConfirm'))) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/claims/${claimId}/waiting-docs`, { stage: checkStage, on: !faltaDoc, note: nota });
      await api.post(`/api/claims/${claimId}/chat`, {
        department: 'payments',
        body: faltaDoc
          ? t('fu.docs.noteCleared')
          : `${t('fu.docs.noteSet')}${nota?.trim() ? ` — ${nota.trim()}` : ''}`,
      }).catch(() => { /* la nota es best-effort: la marca ya quedó */ });
      onSaved();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  /**
   * LOS TRES DESENLACES DEL CHEQUE QUE YA SE ENVIÓ (pedido del dueño, 2026-08-27).
   *
   * Reemplazan a "No payment", que decía sólo que no venía plata y no distinguía QUÉ pasó. Los
   * tres casos son distintos y cada uno manda al claim a un lado distinto del tablero:
   *
   *  · Homeowner with check → el cheque llegó pero lo tiene el HO. Va a la barra "con el cheque"
   *    y ahí se cuentan los días que lo tiene ("cuenta los días de cuánto tiempo lo tiene ahí").
   *  · Check cashed → lo cobró sin autorización: nos debe el fee. Va a "Owed to Us", con su
   *    gráfica de días.
   *  · Check lost → se perdió. NO se mueve de esta cola —hay que llamar por el re-issue— pero la
   *    fila se pinta distinta y el reloj se re-ancla en el día que se marcó perdido.
   *
   * Sólo aparecen con fecha de envío cargada: sin cheque enviado no hay desenlace que marcar (el
   * backend responde `sin_fecha_de_envio` y esto evita ofrecer un botón que va a fallar).
   */
  async function desenlace(outcome: 'ho_with_check' | 'co_with_check' | 'cashed' | 'lost') {
    if (!window.confirm(t(`fu.outcome.${outcome}.confirm`))) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/claims/${claimId}/check-outcome`, { stage: checkStage, outcome });
      await api.post(`/api/claims/${claimId}/chat`, {
        department: 'payments',
        body: t(`fu.outcome.${outcome}.note`),
      }).catch(() => { /* la nota es best-effort: el movimiento ya quedó */ });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError && e.message === 'sin_fecha_de_envio' ? t('fu.outcome.needSent')
        : e instanceof ApiError ? e.message : 'Error');
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--line)', borderRadius: 10, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
        {/* Desde cuándo está en este status — el reloj de la cola. */}
        <span style={{ fontSize: 'var(--fs-xs)' }}>
          <span className="muted">{t('fu.enteredOn')}: </span>
          <strong>{fmtDay(item.status_since)}</strong>
          {item.days_waiting != null && (
            <span style={{ marginLeft: 6, display: 'inline-block', verticalAlign: 'middle' }} title={t('fu.daysIn', { n: String(item.days_waiting) })}>
              <DayDot days={item.days_waiting} greenMax={item.green_max} yellowMax={item.yellow_max} />
            </span>
          )}
        </span>
        {/* FALTA UN DOCUMENTO: se pinta antes que nada, porque explica todo lo que sigue. */}
        {faltaDoc && (
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--info)', fontWeight: 'var(--fw-bold)' }}>
            📄 {t('fu.docs.chip')}
            {item.waiting_docs_note ? `: ${item.waiting_docs_note}` : ''}
            {item.waiting_docs_since ? ` · ${fmtDay(item.waiting_docs_since)}` : ''}
          </span>
        )}
        {/* CHEQUE PERDIDO = RE-ISSUE. Se pinta distinto a propósito: la fila sigue en la misma
            cola, pero el trabajo ya no es "esperar" sino llamar por el reemplazo. */}
        {item.check_state === 'lost' && (
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--purple)', fontWeight: 'var(--fw-bold)' }}>
            ↻ {t('fu.outcome.reissue')}{item.check_state_at ? ` · ${fmtDay(item.check_state_at)}` : ''}
          </span>
        )}
        {item.check_sent_on && item.check_state !== 'lost' && (
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--green)', fontWeight: 'var(--fw-semibold)' }}>
            <Banknote size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('fu.checkSent')} {fmtDay(item.check_sent_on)}
            {item.check_sent_amount_cents != null && <span style={{ fontFamily: 'var(--font-mono)' }}>{` · ${formatUsd(item.check_sent_amount_cents)}`}</span>}
          </span>
        )}
      </div>
      <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)' }}>{t('fu.checkDate')}</div>
      {saved ? (
        <span style={{ color: 'var(--green)', fontSize: 'var(--fs-sm)' }}>
          <Check size={13} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {t('fu.checkDateSaved')}{' '}
          <button className="btn ghost sm" onClick={() => setSaved(false)}>{t('fu.checkDateEdit')}</button>
        </span>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="input" type="date" style={{ width: 155 }} value={date} onChange={(e) => setDate(e.target.value)} />
          <input className="input" type="number" min="0" step="0.01" style={{ width: 130 }}
            placeholder={t('fu.checkAmountPh')} value={amount} onChange={(e) => setAmount(e.target.value)} />
          {/* Sugerido del estimado del seguro (ya leído por la IA): un clic lo pone. */}
          {sug && !amount && (
            <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} title={sug.label}
              onClick={() => setAmount(String(sug.amountCents / 100))}>
              <Bot size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('fu.suggested')}: <span style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(sug.amountCents)}</span>
            </button>
          )}
          <button className="btn sm" disabled={busy} onClick={save}>{busy ? '…' : t('fu.checkDateSave')}</button>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('fu.checkDateHelp')}</span>
          {/* Under deductible: no viene cheque — sale de la cola con la nota en Payments. */}
          {checkStage === 'acv' && (
            <button className="btn ghost sm" disabled={busy} onClick={underDeductible}
              style={{ color: 'var(--orange)', borderColor: 'var(--orange)', fontSize: 'var(--fs-2xs)' }}>
              {t('fu.underDed')}
            </button>
          )}
          {/* Falta un documento: la espera sigue, pero se ve POR QUÉ está parada. En todas las etapas. */}
          <button className="btn ghost sm" disabled={busy} onClick={() => void toggleFaltaDoc()}
            title={faltaDoc ? t('fu.docs.clearHelp') : t('fu.docs.help')}
            style={{ color: 'var(--info)', borderColor: 'var(--info)', fontSize: 'var(--fs-2xs)',
                     ...(faltaDoc ? { background: 'var(--info)', color: '#fff' } : {}) }}>
            📄 {faltaDoc ? t('fu.docs.clearBtn') : t('fu.docs.btn')}
          </button>
          {/* Payment received: el cheque ya llegó — se elige cuál de los que entraron y con qué fecha. */}
          <button className="btn ghost sm" disabled={busy} onClick={() => setEligiendo(true)} title={t('fu.received.help')}
            style={{ color: 'var(--green)', borderColor: 'var(--green)', fontSize: 'var(--fs-2xs)' }}>
            {t('fu.received.btn')}
          </button>
        </div>
      )}
      {/* QUÉ PASÓ CON EL CHEQUE — sólo si ya se envió uno Y todavía no llegó. Ofrecerlos sobre un
          cheque ya recibido deja marcar como perdido algo que está en la mano. */}
      {item.check_sent_on && !item.check_received_on && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4, borderTop: '1px dashed var(--line)' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('fu.outcome.title')}</span>
          <button className="btn ghost sm" disabled={busy} onClick={() => void desenlace('ho_with_check')}
            title={t('fu.outcome.ho_with_check.help')}
            style={{ color: 'var(--purple)', borderColor: 'var(--purple)', fontSize: 'var(--fs-2xs)' }}>
            {t('fu.outcome.ho_with_check.btn')}
          </button>
          {/* El CONTRATISTA se llevó el cheque (pedido del dueño 2026-09-10). Mismo destino de
              pantalla que el HO: la barra "Homeowner / contractor with check" ya leía este status
              y la vista de Payments lo acepta desde la mig 0431. */}
          <button className="btn ghost sm" disabled={busy} onClick={() => void desenlace('co_with_check')}
            title={t('fu.outcome.co_with_check.help')}
            style={{ color: 'var(--purple)', borderColor: 'var(--purple)', fontSize: 'var(--fs-2xs)' }}>
            {t('fu.outcome.co_with_check.btn')}
          </button>
          <button className="btn ghost sm" disabled={busy} onClick={() => void desenlace('cashed')}
            title={t('fu.outcome.cashed.help')}
            style={{ color: 'var(--red)', borderColor: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>
            {t('fu.outcome.cashed.btn')}
          </button>
          {/* Ya marcado perdido: volver a apretarlo sólo puede reiniciar el reloj del re-issue. */}
          {item.check_state !== 'lost' && (
            <button className="btn ghost sm" disabled={busy} onClick={() => void desenlace('lost')}
              title={t('fu.outcome.lost.help')}
              style={{ color: 'var(--orange)', borderColor: 'var(--orange)', fontSize: 'var(--fs-2xs)' }}>
              {t('fu.outcome.lost.btn')}
            </button>
          )}
        </div>
      )}
      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</div>}
      {eligiendo && (
        <PaymentReceivedDialog
          claimId={claimId} stage={checkStage}
          onDone={() => { setEligiendo(false); onSaved(); }}
          onCancel={() => setEligiendo(false)}
        />
      )}
      {confirmando && (
        <CheckConfirmDialog
          needsReason={pideRazon}
          onConfirm={(c) => { void saveConfirmado(c); }}
          onCancel={() => { setConfirmando(false); setPideRazon(false); }}
        />
      )}
    </div>
  );
}

/** Pestañas de la cola. Normales: All / Sin fecha / Enviados. Repair: + con pago / sin pago. */
type QueueTab = 'all' | 'notSent' | 'sent' | 'waitingPay' | 'noPay';

const NO_PAY_STATUS = 'Repair (No Payment to HO or CO)';

function tabOf(it: FollowUpItem): Exclude<QueueTab, 'all'> {
  if (it.current_status === NO_PAY_STATUS) return 'noPay';
  if (it.check_sent_on) return 'sent';
  return 'notSent';
}

/**
 * Cola de una etapa de pago: pestañas Sin fecha / Enviados (Repair además Con pago / Sin pago),
 * fecha de entrada al status + fecha y monto del cheque, y al desplegar el Payments Department
 * Update (recogible) + el módulo de email con las plantillas de la vista. El ⚙ de arriba
 * configura plantillas y días (cadencia del follow-up + umbrales urgente/manager).
 */
export function FollowUpQueue({ stage, titleKey }: { stage: string; titleKey: string }) {
  const { t } = useI18n();
  const [items, setItems] = useState<FollowUpItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState<QueueTab>('all');
  const [settings, setSettings] = useState(false);
  const [view, setView] = useState<'cards' | 'lines'>(() => (localStorage.getItem('pay.view') as 'cards' | 'lines') || 'lines');
  const setViewPersist = (v: 'cards' | 'lines') => { setView(v); localStorage.setItem('pay.view', v); };
  const cd = useClaimDrawer();
  const isRepair = stage === 'approved-repair';
  // ?focus=<claimId>: llega desde las barras del dashboard de Payments (2026-08-10) — abre la
  // fila de ese cliente y la scrollea a la vista.
  const [searchParams] = useSearchParams();
  const focus = searchParams.get('focus');

  async function load() {
    setLoaded(false);
    try {
      setItems((await api.get<{ claims: FollowUpItem[] }>(`/api/followups/queue/${stage}`)).claims);
    } catch { setItems([]); }
    setLoaded(true);
  }
  useEffect(() => { setSearch(''); setOpen(focus ? { [focus]: true } : {}); setTab('all'); load(); /* eslint-disable-next-line */ }, [stage]);
  useEffect(() => {
    if (!loaded || !focus) return;
    const el = document.getElementById(`fu-${focus}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loaded, focus]);

  const q = search.trim().toLowerCase();
  /* El buscador entra al scope. Las cuatro pestañas de abajo ya contaban sobre `searched` (bien),
     pero los botones Mía/All del selector de persona seguían contando la cola entera. */
  const sc = useScope(items, (i) => i.assigned_person, {
    getTeam: (i) => i.claim_team,
    filtro: (i) => !q || `${i.claim_number ?? ''} ${i.homeowner_name ?? ''} ${i.contractor_name ?? ''}`.toLowerCase().includes(q),
  });
  const searched = sc.shown;
  const counts = {
    all: searched.length,
    notSent: searched.filter((i) => tabOf(i) === 'notSent').length,
    sent: searched.filter((i) => tabOf(i) === 'sent').length,
    waitingPay: searched.filter((i) => tabOf(i) !== 'noPay').length,
    noPay: searched.filter((i) => tabOf(i) === 'noPay').length,
  };
  const shown = tab === 'all' ? searched
    : tab === 'waitingPay' ? searched.filter((i) => tabOf(i) !== 'noPay')
    : searched.filter((i) => tabOf(i) === tab);

  // Selección múltiple sobre lo que está filtrado/visible.
  const sel = useBulkSelect(shown.map((i) => i.id));

  const viewToggle = (
    <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
      <button className={`btn ghost sm${view === 'lines' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setViewPersist('lines')}><List size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sup.viewLines')}</button>
      <button className={`btn ghost sm${view === 'cards' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setViewPersist('cards')}><LayoutGrid size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sup.viewCards')}</button>
    </div>
  );

  const tabBtn = (k: QueueTab, label: string, n: number) => (
    <button className="btn ghost sm" onClick={() => setTab(k)}
      style={{ borderRadius: 0, borderBottom: tab === k ? '2px solid var(--accent)' : '2px solid transparent', fontWeight: tab === k ? 700 : 400, color: tab === k ? 'var(--accent)' : undefined }}>
      {label} <span className="muted">({n})</span>
    </button>
  );

  return (
    <BulkProvider sel={sel}>
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div className="card" style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <BulkAllCheck label={t('bulk.all')} />
          <h2 className="card-title" style={{ margin: 0 }}>
            {t(titleKey)} {loaded && <span className="badge gray">{shown.length}</span>}
          </h2>
          <span style={{ flex: 1 }} />
          {viewToggle}
          <input className="input" style={{ maxWidth: 260 }} placeholder={t('claims.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} />
          {/* ⚙ de ESTA vista: plantillas de email + cadencia del follow-up y umbrales. */}
          {VIEW_KEY[stage] && <ViewSettingsButton onClick={() => setSettings(true)} />}
        </div>
        {settings && VIEW_KEY[stage] && (
          <ViewSettingsModal deptKey="payments" viewKey={VIEW_KEY[stage]} label={t(titleKey)} onClose={() => setSettings(false)} />
        )}
        {/* Pestañas: sin fecha / enviados (Repair además con/sin pago). */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          {tabBtn('all', t('fu.tabAll'), counts.all)}
          {isRepair && tabBtn('waitingPay', t('fu.tabWaitingPay'), counts.waitingPay)}
          {isRepair && tabBtn('noPay', t('fu.tabNoPay'), counts.noPay)}
          {tabBtn('notSent', t('fu.tabNotSent'), counts.notSent)}
          {tabBtn('sent', t('fu.tabSent'), counts.sent)}
        </div>
        {loaded && <ScopeTabs {...sc} />}
        {!loaded ? (
          <div className="muted">{t('common.loading')}</div>
        ) : shown.length === 0 ? (
          <div className="muted">{t('queue.empty')}</div>
        ) : (
          view === 'cards' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10, alignItems: 'start' }}>
              {shown.map((it) => (
                <div key={it.id} id={`fu-${it.id}`} style={{ scrollMarginTop: 80 }}>
                  <FollowUpRow item={it} stage={stage} layout="card" open={!!open[it.id]} onToggle={() => setOpen((o) => ({ ...o, [it.id]: !o[it.id] }))} onLogged={load} onClient={cd.open} />
                </div>
              ))}
            </div>
          ) : (
            /* Tabla alineada: leyenda arriba + líneas verticales; columnas compartidas con las filas. */
            <div className="fuq-table">
              <QueueLegend />
              {shown.map((it) => (
                <div key={it.id} id={`fu-${it.id}`} style={{ scrollMarginTop: 80 }}>
                  <FollowUpRow item={it} stage={stage} open={!!open[it.id]} onToggle={() => setOpen((o) => ({ ...o, [it.id]: !o[it.id] }))} onLogged={load} onClient={cd.open} />
                </div>
              ))}
            </div>
          )
        )}
      </div>
      {/* Acciones en lote: la cola de pago es del depto de Payments. */}
      <ClaimBulkBar sel={sel} dept="payments" onDone={load} />
      {cd.drawer}
    </div>
    </BulkProvider>
  );
}

/**
 * LOS ESTIMADOS DEL SEGURO, A LA MANO — arriba de todo en la fila de la cola.
 *
 * Pedido de Payments (2026-08-27): "en la sección donde buscamos los cheques como ACV,
 * depreciación, quiero q salga recogido los files pero solo los estimates del insurance, de
 * manera que yo pueda darle click y abra el drawer con los estimados, coloca esto de primero
 * debajo del nombre".
 *
 * Es el documento con el que se contrasta el cheque que se está esperando, y hasta hoy había que
 * salir de la cola para verlo. Solo los DEL SEGURO (`doc_type = 'estimate'`): los nuestros
 * (`proactive_estimate`) son lo que pedimos, no lo que el carrier aprobó — mezclarlos acá haría
 * dudar de cuál es el número bueno.
 *
 * Se carga al DESPLEGAR la fila, no con la lista: la cola trae decenas de claims y pedir los
 * archivos de cada uno serían decenas de requests para algo que casi nunca se mira cerrado.
 */
function InsuranceEstimatesStrip({ claimId, onOpen }: { claimId: string; onOpen?: (id: string, tab?: string) => void }) {
  const { t } = useI18n();
  type Fin = { rcv_amount_cents: number | null; acv_cents: number | null; net_claim_cents: number | null } | null;
  type F = { id: string; file_name: string | null; doc_type: string; created_at: string; financials?: Fin };
  const [files, setFiles] = useState<F[] | null>(null);
  const [viendo, setViendo] = useState<F | null>(null);
  void onOpen; // el clic ya NO abre el claim: abre el estimado (ver comentario de arriba)

  useEffect(() => {
    let alive = true;
    api.get<{ files: F[] }>(`/api/claims/${claimId}/files`)
      .then((r) => { if (alive) setFiles(r.files.filter((f) => f.doc_type === 'estimate')); })
      .catch(() => { if (alive) setFiles([]); });
    return () => { alive = false; };
  }, [claimId]);

  if (!files || files.length === 0) return null;

  const monto = (v: number | null | undefined) => (v == null ? '—' : formatUsd(v));

  return (
    <div style={{ marginTop: 10, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)' }}>
          <FileText size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('fu.insEstimates')}
        </span>
        <span className="badge gray">{files.length}</span>
      </div>
      {/* UNO DEBAJO DEL OTRO, con sus montos a la vista. Antes iban en línea con flex-wrap y los
          nombres largos (los del carrier lo son siempre) se cortaban a la mitad. */}
      <div style={{ display: 'grid', gap: 4 }}>
        {files.map((f) => (
          <button
            key={f.id}
            className="btn ghost sm"
            style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'left', padding: '5px 8px' }}
            title={f.file_name ?? ''}
            onClick={(e) => { e.stopPropagation(); setViendo(f); }}
          >
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--fs-2xs)' }}>
              {f.file_name || t('fu.insEstimateFile')}
              <span className="muted">{` · ${fmtDay(f.created_at)}`}</span>
            </span>
            {/* Los dos números que Payments contrasta contra el cheque, SIN abrir el archivo. */}
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
              {t('fu.estRcv')}: <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>{monto(f.financials?.rcv_amount_cents)}</strong>
            </span>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
              {t('fu.estAcv')}: <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>{monto(f.financials?.acv_cents ?? f.financials?.net_claim_cents)}</strong>
            </span>
          </button>
        ))}
      </div>
      {/* EL CLIC ABRE EL ESTIMADO, NO EL CLAIM. Antes llamaba onOpen(claimId,'files') y te sacaba
          de la cola al drawer del cliente (reporte del dueño 2026-08-27: "cuando le doy click al
          estimado me abre el claim, esta mal, deberia abrir el estimado embebido en el centro de
          la pantalla"). FileViewerModal ya hacía exactamente eso — resuelve la URL firmada y pinta
          el PDF centrado sobre la pantalla; no había que inventar nada. */}
      {viendo && (
        <FileViewerModal fileId={viendo.id} title={viendo.file_name} onClose={() => setViendo(null)} />
      )}
    </div>
  );
}

export function FollowUpRow({
  item, stage, open, onToggle, onLogged, onClient, layout = 'grid', hideCoChips = false,
}: {
  item: FollowUpItem;
  stage: string;
  open: boolean;
  onToggle: () => void;
  onLogged: () => void;
  onClient?: (id: string, tab?: string) => void;
  /** 'grid' = fila de la tabla alineada (con leyenda); 'card' = tarjeta suelta apilada. */
  layout?: 'grid' | 'card';
  /** El To Do separa lo del CONTRACTOR en su propia sección (feedback 2026-07-29):
   *  ahí las filas del claim van SIN los chips 👷 y el nombre abre el chat normal. */
  hideCoChips?: boolean;
}) {
  const { t } = useI18n();
  // Canal de updates: si el item viene de un To Do de departamento (stage = key del depto), usa ESE
  // depto; si es una etapa de pago (acv/…), usa el depto del usuario (o Payments). Así el módulo de
  // updates y la cadencia de follow-up siempre coinciden con el departamento del To Do.
  const myDept = useMyDefaultDepartment();
  const DEPT_KEYS = ['claims', 'payments', 'supplements', 'scheduling', 'intake'];
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const deptKey = DEPT_KEYS.includes(stage) ? stage : (myDept?.key || 'payments');
  const deptTitle = `${DEPT_KEYS.includes(stage) ? cap(stage) : (myDept?.name || 'Payments')} Department Updates`;
  const checkStage = CHECK_STAGE[stage];
  // 'Repair (No Payment...)' no espera ningún cheque: no hay fecha/monto que registrar.
  const tracksCheck = Boolean(checkStage) && item.current_status !== NO_PAY_STATUS;

  // Nombre de la fila y quién responde. En PAGOS (etapas acv/depreciation/mortgage/
  // approved-repair/supplement, o el To Do con dept=payments) el que responde es el PAYMENTS
  // MANAGER — assigned_person, "la persona del board de Payments" — no el account manager
  // general del claim (pedido 2026-07-29: "en el to do de payments quiero que salga el payments
  // manager"). En Claims/Supplements/Scheduling/Intake se mantiene el account manager.
  const clientName = item.homeowner_name || item.claim_number || `#${item.id}`;
  const isPaymentsRow = stage === 'payments' || stage in CHECK_STAGE;
  const manager = isPaymentsRow
    ? (item.assigned_person || item.account_manager || '—')
    : (item.account_manager || item.assigned_person || '—');
  // El CONTRACTOR manda en la fila: mensajes SIN RESPONDER (SLA 1 día) y/o follow-up vencido.
  const coMsgs = item.co_msg_count ?? 0;
  const coPending = !hideCoChips && (coMsgs > 0 || Boolean(item.contractor_due));
  const coDays = item.contractor_overdue_days ?? 0;
  const openContractor = (e: React.MouseEvent) => { e.stopPropagation(); onClient?.(item.id, 'contractor'); };
  const client = (
    <span style={{ minWidth: 0 }}>
      {onClient ? (
        /* Con algo del CONTRACTOR pendiente, abrir el cliente cae directo en su chat (pedido). */
        <ClientNameButton claimId={item.id} name={clientName} onOpen={(id) => onClient(id, coPending ? 'contractor' : undefined)} />
      ) : (
        <Link to={`/claims/${item.id}`} onClick={(e) => e.stopPropagation()}>
          <strong>{clientName}</strong>
        </Link>
      )}
      {item.claim_number && (
        <Link to={`/claims/${item.id}`} onClick={(e) => e.stopPropagation()} className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'block', fontFamily: 'var(--font-mono)' }}>
          {item.claim_number}
        </Link>
      )}
      {/* Cola Mortgage: el banco y el loan number EN la fila (los pide el banco al endosar). */}
      {stage === 'mortgage' && (item.mortgage_name || item.mortgage_company || item.mortgage_loan_number) && (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'block' }}>
          <Landmark size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />{' '}
          {item.mortgage_name || item.mortgage_company || '—'}
          {item.mortgage_loan_number ? ` · Loan #${item.mortgage_loan_number}` : ''}
        </span>
      )}
    </span>
  );
  // To Do de un departamento (stage = key del depto ≠ payments): la columna
  // muestra el status del claim EN ESE board (claim status para claims,
  // supplement status para supplements…), no el status de pagos.
  const isDeptBoard = DEPT_KEYS.includes(stage) && stage !== 'payments';
  const paymentBadge = isDeptBoard ? (
    <span
      className={`badge ${statusColor(item.dept_status)}`}
      style={{ fontSize: 'var(--fs-2xs)', justifySelf: 'start', alignSelf: 'start' }}
      title={statusColLabel(t, stage)}
    >
      <ClipboardList size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {item.dept_status || '—'}
    </span>
  ) : (
    <>
      <span className={`badge ${statusColor(item.current_status)}`} style={{ fontSize: 'var(--fs-2xs)', justifySelf: 'start', alignSelf: 'start' }} title="Payment status">
        <Banknote size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {item.current_status || '—'}
      </span>
      {/* Info del cheque YA registrada (fecha + monto) → no hay nada que perseguir. */}
      {item.check_sent_on && (
        <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', color: 'var(--green)' }} title={t('fu.checkSentHint')}>
          <Banknote size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('fu.checkSent')} {fmtDay(item.check_sent_on)}
          {item.check_sent_amount_cents != null && ` · ${formatUsd(item.check_sent_amount_cents)}`}
        </span>
      )}
      {/* Repair sin pago: nada que trackear (etiqueta para distinguirlo de un olvido). */}
      {item.current_status === NO_PAY_STATUS && (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('fu.noPayBadge')}</span>
      )}
    </>
  );
  // To Do de CLAIMS/SUPPLEMENTS: se trabaja por follow-ups vencidos → los días del círculo son
  // los días DE ATRASO del próximo follow-up (0 = vence hoy), con umbrales más apretados (0/2:
  // hoy en verde, 1-2 días tarde amarillo, 3+ rojo). En el resto, días EN el status (⚙).
  const isClaimsTodo = FU_TODO_DEPTS.includes(stage);
  const due = daysDue(item.next_follow_up_at);
  const dot = isClaimsTodo
    ? <DayDot days={due} greenMax={0} yellowMax={2} />
    : <DayDot days={item.days_waiting} greenMax={item.green_max} yellowMax={item.yellow_max} />;
  // Chips del CONTRACTOR: abrirlos cae DIRECTO en su chat (pedido del dueño).
  const coChip = coPending ? (
    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifySelf: 'start' }}>
      {coMsgs > 0 && (
        <button type="button" className="badge" onClick={openContractor} title={t('ci.msgChipHint')}
          style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', border: 0, cursor: 'pointer',
            background: item.co_msg_overdue ? 'var(--red-soft)' : 'var(--orange-soft)',
            color: item.co_msg_overdue ? 'var(--red)' : 'var(--orange)' }}>
          <HardHat size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ci.msgChip', { n: String(coMsgs), h: String(item.co_msg_hours ?? 0) })}
        </button>
      )}
      {item.contractor_due && (
        <button type="button" className="badge" onClick={openContractor} title={t('ci.followupChipHint')}
          style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', border: 0, cursor: 'pointer',
            background: coDays >= 3 ? 'var(--red-soft)' : 'var(--orange-soft)', color: coDays >= 3 ? 'var(--red)' : 'var(--orange)' }}>
          <HardHat size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ci.followupChip', { n: String(coDays) })}
        </button>
      )}
    </span>
  ) : null;

  const line = layout === 'card' ? (
    /* Tarjeta suelta (vista ▦ y pantallas sin tabla): mismo contenido, apilado. */
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', cursor: 'pointer', flexWrap: 'wrap' }} onClick={onToggle}>
      <BulkRowCheck id={item.id} />
      <span style={{ width: 12, color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
      {client}
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>{paymentBadge}{coChip}</span>
      {isClaimsTodo ? (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }} title={t('fu.colNextFu')}><AlarmClock size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {fmtDay(item.next_follow_up_at)}</span>
      ) : (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }} title={t('fu.enteredOn')}><CalendarDays size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {fmtDay(item.status_since)}</span>
      )}
      {dot}
      <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><User size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {manager}</span>
    </div>
  ) : isClaimsTodo ? (
    /* Fila del To Do de Claims: último + próximo follow-up y días de atraso (leyenda fuq-fu). */
    <div className="fuq-cols fuq-fu fuq-line" onClick={onToggle}>
      <span className="fuq-cell fuq-c1">
        <BulkRowCheck id={item.id} />
        <span style={{ width: 12, color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
      </span>
      <span className="fuq-cell">{client}</span>
      <span className="fuq-cell">{paymentBadge}{coChip}</span>
      <span className="fuq-cell" style={{ fontSize: 'var(--fs-xs)' }} title={t('fu.colLastFu')}>
        {item.last_follow_up_at ? fmtDay(item.last_follow_up_at) : <span className="muted">{t('fu.fuNever')}</span>}
      </span>
      <span className="fuq-cell" style={{ fontSize: 'var(--fs-xs)', color: due > 0 ? 'var(--red)' : undefined, fontWeight: due > 0 ? 'var(--fw-bold)' : undefined }} title={t('fu.colNextFu')}>
        {fmtDay(item.next_follow_up_at)}
      </span>
      <span className="fuq-cell" style={{ alignItems: 'center' }}>{dot}</span>
      <span className="fuq-cell muted" style={{ fontSize: 'var(--fs-2xs)' }}><User size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {manager}</span>
    </div>
  ) : (
    /* Fila de la tabla: mismas columnas que la leyenda (QueueLegend) → todo alineado. */
    <div className="fuq-cols fuq-line" onClick={onToggle}>
      <span className="fuq-cell fuq-c1">
        {/* Selección múltiple: sale solo en las pantallas que la habilitan (BulkProvider). */}
        <BulkRowCheck id={item.id} />
        <span style={{ width: 12, color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
      </span>
      <span className="fuq-cell">{client}</span>
      <span className="fuq-cell">{paymentBadge}</span>
      <span className="fuq-cell" style={{ fontSize: 'var(--fs-xs)' }} title={t('fu.enteredOn')}>{fmtDay(item.status_since)}</span>
      <span className="fuq-cell" style={{ alignItems: 'center' }}>{dot}</span>
      <span className="fuq-cell muted" style={{ fontSize: 'var(--fs-2xs)' }}><User size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {manager}</span>
    </div>
  );

  return (
    <div className={layout === 'card' ? undefined : 'fuq-rowwrap'}
      style={layout === 'card' ? { border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' } : undefined}>
      {line}

      {open && (
        <div style={{ padding: '0 14px 14px 38px', borderTop: '1px solid var(--border)' }}>
          {/* La tarjeta de SU VISTA del board (First Estimate, Supplementing…), tal cual se vería
              entrando por el board. Se carga al desplegar; en los deptos sin tarjeta rica no pinta nada. */}
          {/* Primero de todo: los estimados del seguro (pedido 2026-08-27). */}
          <InsuranceEstimatesStrip claimId={item.id} onOpen={onClient} />
          <BoardCardBridge claimId={item.id} deptKey={deptKey} deptStatus={item.dept_status} onChanged={onLogged} />
          {/* Cola Mortgage: los datos del banco editables ACÁ mismo (nombre, loan #, PO box). */}
          {stage === 'mortgage' && <div style={{ marginTop: 12 }}><MortgageBox claimId={item.id} claim={item} onSaved={onLogged} /></div>}
          {tracksCheck && <CheckSentBox claimId={item.id} checkStage={checkStage!} item={item} onSaved={onLogged} />}
          <InsuranceAdjusterBox item={item} onSaved={onLogged} />
          {/* Payments Department Update — recogible/desplegable. El botón "Follow up" del propio
              update registra el follow-up (reemplaza al log viejo, que se quitó a pedido). */}
          <details open style={{ border: '1px solid var(--line)', borderRadius: 10, marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)', padding: '8px 12px' }}><MessageSquare size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {deptTitle}</summary>
            <div style={{ padding: '0 12px 12px' }}>
              <ClaimChat claimId={item.id} channel={deptKey} title={deptTitle} />
            </div>
          </details>
          {/* Email — el módulo de siempre, con las plantillas de ESTA vista (⚙). */}
          <details style={{ border: '1px solid var(--line)', borderRadius: 10, marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)', padding: '8px 12px' }}><Mail size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('em.communication')}</summary>
            <div style={{ padding: '0 12px 12px' }}>
              <ClaimEmailBox
                claimId={item.id}
                claimNumber={item.claim_number}
                departmentKey="payments"
                templatesScope={VIEW_KEY[stage] ? { deptKey: 'payments', viewKey: VIEW_KEY[stage] } : undefined}
              />
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

/**
 * Aseguradora + info del adjuster (desk) y del field adjuster, editable desde el
 * follow-up. Al guardar actualiza el claim y publica el cambio en el chat Payments
 * (que también aparece en General). (#2)
 */
function InsuranceAdjusterBox({ item, onSaved }: { item: FollowUpItem; onSaved: () => void }) {
  const { t } = useI18n();
  const [ins, setIns] = useState(item.insurance_company ?? '');
  const [adj, setAdj] = useState(item.adjuster_info ?? '');
  const [field, setField] = useState(item.field_adjuster_info ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const dirty =
    ins !== (item.insurance_company ?? '') ||
    adj !== (item.adjuster_info ?? '') ||
    field !== (item.field_adjuster_info ?? '');

  async function save() {
    setBusy(true); setMsg(null);
    try {
      await api.post(`/api/claims/${item.id}/insurance-adjuster`, {
        insuranceCompany: ins.trim() || null,
        adjusterInfo: adj.trim() || null,
        fieldAdjusterInfo: field.trim() || null,
      });
      setMsg('ok');
      onSaved();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Error: ${e.message}` : 'Error.');
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--card)' }}>
      <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)', marginBottom: 8 }}><Building2 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('fu.insAdjTitle')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label style={{ fontSize: 'var(--fs-2xs)' }}>
          {/* El nombre es LINK a la tarjeta de la aseguradora (KB de Monday: teléfonos, emails,
              cómo filear, notas) — pedido 2026-08-10; el input sigue siendo editable. */}
          <div className="muted" style={{ marginBottom: 2, display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span>{t('fu.insurance')}</span>
            {(ins || item.insurance_company) && <span style={{ fontSize: 'var(--fs-2xs)' }} onClick={(e) => e.preventDefault()}><InsuranceLink name={ins || item.insurance_company} /></span>}
          </div>
          <input className="input" style={{ width: '100%' }} value={ins} onChange={(e) => setIns(e.target.value)} placeholder={t('fu.insurance')} />
        </label>
        <label style={{ fontSize: 'var(--fs-2xs)' }}>
          <div className="muted" style={{ marginBottom: 2 }}>{t('fu.adjuster')}</div>
          <input className="input" style={{ width: '100%' }} value={adj} onChange={(e) => setAdj(e.target.value)} placeholder={t('fu.adjusterPh')} />
        </label>
        <label style={{ fontSize: 'var(--fs-2xs)', gridColumn: '1 / -1' }}>
          <div className="muted" style={{ marginBottom: 2 }}>{t('fu.fieldAdjuster')}</div>
          <input className="input" style={{ width: '100%' }} value={field} onChange={(e) => setField(e.target.value)} placeholder={t('fu.adjusterPh')} />
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <button className="btn sm" disabled={busy || !dirty} onClick={save}>{busy ? '…' : t('fu.saveAdj')}</button>
        {msg && <span style={{ fontSize: 'var(--fs-2xs)', color: msg === 'ok' ? 'var(--green)' : 'var(--red)' }}>{msg === 'ok' ? t('fu.savedGeneral') : msg}</span>}
      </div>
    </div>
  );
}

/**
 * SECCIÓN "Follow-ups del CONTRATISTA" del To Do (feedback 2026-07-29): separada de los
 * follow-ups del claim. Cada fila se despliega con la flechita y muestra el CHAT del
 * contractor (las últimas notas) — postear ahí ES el follow-up: comparte a JobFlow, responde
 * los mensajes abiertos y reinicia el reloj de 3 días.
 */
export function ContractorFollowUpsSection({ items, onLogged, onClient }: {
  items: FollowUpItem[];
  onLogged: () => void;
  onClient?: (id: string, tab?: string) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  if (!items.length) return null;
  return (
    <div className="card" style={{ borderLeft: '4px solid var(--orange)' }}>
      <h2 className="card-title" style={{ color: 'var(--orange)' }}>
        <HardHat size={16} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('diary.coSection')} <span className="badge gray">{items.length}</span>
      </h2>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 8 }}>{t('diary.coSectionHelp')}</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {items.map((it) => {
          const coMsgs = it.co_msg_count ?? 0;
          const coDays = it.contractor_overdue_days ?? 0;
          const isOpen = !!open[it.id];
          return (
            <div key={it.id} style={{ border: '1px solid var(--line)', borderRadius: 10 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', cursor: 'pointer' }}
                onClick={() => setOpen((o) => ({ ...o, [it.id]: !o[it.id] }))}
              >
                <span style={{ width: 12, color: 'var(--muted)' }}>{isOpen ? '▾' : '▸'}</span>
                <span onClick={(e) => e.stopPropagation()}>
                  <ClientNameButton claimId={it.id} name={it.homeowner_name || it.claim_number || `#${it.id}`}
                    onOpen={(id) => onClient?.(id, 'contractor')} />
                </span>
                {it.claim_number && (
                  <Link to={`/claims/${it.id}`} onClick={(e) => e.stopPropagation()} className="muted" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}>{it.claim_number}</Link>
                )}
                {it.contractor_name && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--surface-2)' }}><Wrench size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {it.contractor_name}</span>}
                {coMsgs > 0 && (
                  <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)',
                    background: it.co_msg_overdue ? 'var(--red-soft)' : 'var(--orange-soft)',
                    color: it.co_msg_overdue ? 'var(--red)' : 'var(--orange)' }}>
                    {t('ci.msgChip', { n: String(coMsgs), h: String(it.co_msg_hours ?? 0) })}
                  </span>
                )}
                {it.contractor_due && (
                  <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)',
                    background: coDays >= 3 ? 'var(--red-soft)' : 'var(--orange-soft)', color: coDays >= 3 ? 'var(--red)' : 'var(--orange)' }}>
                    {t('ci.followupChip', { n: String(coDays) })}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><User size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {it.account_manager || it.assigned_person || '—'}</span>
              </div>
              {isOpen && (
                <div style={{ borderTop: '1px solid var(--line)', padding: 10 }} onClick={(e) => e.stopPropagation()}>
                  {/* Contractor board y General update UNO AL LADO DEL OTRO (feedback
                      2026-07-29): se deja el follow-up del CO viendo el contexto del claim
                      sin cambiar de pantalla. En pantallas angostas se apilan. */}
                  <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'start' }}>
                    <ClaimChat claimId={it.id} channel="contractor" title={t('diary.coChatTitle')} noFollowUp summarySignal={0} />
                    <ClaimChat claimId={it.id} channel={null} title={t('diary.coGeneralTitle')} noFollowUp summarySignal={0} />
                  </div>
                  <div style={{ marginTop: 6, textAlign: 'right' }}>
                    <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={onLogged}>↻ {t('diary.coRefresh')}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
